/**
 * Branchen-Landingpages (/branchen/[slug]).
 *
 * Zielgruppen-Einstieg: "Ich bin Handwerker / Wirt / Arzt – was bringt mir Software?"
 * Jede Branche hat eigene Schmerzpunkte, eigene Sprache und eigene Suchanfragen.
 * Deshalb bekommt jede Branche echten, eigenständigen Inhalt statt Textbausteine.
 */

import type { LandingContent } from "./landing";

export const industries: LandingContent[] = [
  // ────────────────────────────────────────────────────────────────
  {
    slug: "handwerk",
    kundenHinweis:
      "Wer „Installateur Notdienst“ oder „Elektriker in der Nähe“ sucht, braucht jetzt jemanden – wir schalten Anzeigen im eigenen Einzugsgebiet und messen sie an echten Anrufen. Für Großprojekte ab 5.000 € gibt es das Großauftrag-System mit Garantie: 5 Großaufträge in 90 Tagen, sonst arbeiten wir kostenlos weiter.",
    kundenLink: { href: "/grossauftrag-system", label: "Zum Großauftrag-System" },
    icon: "hammer",
    title: "Handwerksbetriebe",
    navTitle: "Handwerk",
    belege: ["elektrobuga"],
    tagline: "Weniger Zettel, mehr Aufträge – Software für die Baustelle.",
    summary:
      "Digitale Auftragsabwicklung für Elektriker, Installateure, Tischler, Maler und Dachdecker: Angebote in Minuten, Zeiterfassung vom Handy, Fotos direkt von der Baustelle.",
    keywords: [
      "Software für Handwerksbetriebe",
      "Handwerkersoftware Österreich",
      "Auftragsverwaltung Handwerk",
      "Digitalisierung Handwerk Wien",
      "App für Monteure",
    ],
    metaTitle: "Software für Handwerksbetriebe",
    metaDescription:
      "Angebote in Minuten, mobile Zeiterfassung, Fotodoku von der Baustelle. Ersetzt Excel und Zettel. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Für Handwerksbetriebe",
    heroHeadline: "Ihr Handwerk gehört auf die Baustelle – nicht ins Büro bis 22 Uhr",
    heroSub:
      "Angebote schreiben, Stunden zusammensuchen, Material nachtragen: Das frisst jeden Abend Stunden. Wir bauen Ihnen ein System, das diese Arbeit übernimmt – bedienbar mit dreckigen Fingern am Handy.",
    pains: [
      {
        title: "Angebote dauern Tage statt Minuten",
        text: "Der Kunde ruft an, Sie fahren hin, messen aus – und dann liegt der Zettel eine Woche im Auto. Bis das Angebot rausgeht, hat der Mitbewerber den Auftrag.",
      },
      {
        title: "Stundenzettel sind ein Krimi",
        text: "Am Monatsende sammeln Sie zerknitterte Zettel ein, entziffern Handschriften und rechnen nach. Was nicht aufgeschrieben wurde, wird nie verrechnet – und das ist bares Geld.",
      },
      {
        title: "Niemand weiß, was auf welcher Baustelle liegt",
        text: "Material wurde bestellt, aber wohin geliefert? Welcher Monteur hat den Schlüssel? Jede zweite Frage kostet drei Anrufe.",
      },
      {
        title: "Nachweise fehlen, wenn es zählt",
        text: "Der Kunde reklamiert, aber niemand hat Fotos vom Zustand vorher. Ohne Dokumentation steht Aussage gegen Aussage – und Sie zahlen drauf.",
      },
    ],
    solutions: [
      {
        title: "Angebot noch beim Kunden fertig",
        text: "Leistungen aus Ihrem eigenen Katalog antippen, Menge eingeben, fertig. Das Angebot geht per E-Mail raus, bevor Sie wieder im Auto sitzen.",
      },
      {
        title: "Zeiterfassung mit zwei Taps",
        text: "Monteur öffnet die App, tippt auf die Baustelle, tippt auf Start. Am Monatsende steht die Abrechnung fertig da – inklusive Zuschlägen und Fahrtzeiten.",
      },
      {
        title: "Baustellendoku per Foto",
        text: "Foto machen, Baustelle zuordnen, fertig. Alles landet automatisch beim richtigen Auftrag und ist Jahre später noch auffindbar.",
      },
    ],
    sections: [
      {
        heading: "Warum Standardsoftware im Handwerk oft scheitert",
        body:
          "Die großen Handwerkerprogramme können alles – und genau das ist das Problem. Sie zahlen monatlich für Module, die Sie nie öffnen, kämpfen mit Oberflächen, die für den Bürorechner gebaut wurden, und Ihre Monteure benutzen sie am Ende einfach nicht. Software, die auf der Baustelle nicht funktioniert, wird nicht verwendet – und nicht verwendete Software ist verlorenes Geld. Wir gehen anders vor: Wir schauen uns an, wie Ihr Betrieb tatsächlich arbeitet, und bauen genau die drei bis fünf Abläufe, die täglich Zeit fressen. Nicht mehr. Was Sie nicht brauchen, bauen wir nicht – und Sie bezahlen es auch nicht.",
      },
      {
        heading: "Mobil zuerst gedacht, nicht mobil nachgerüstet",
        body:
          "Ein Monteur hat eine Hand frei, steht im Halbdunkel und trägt Handschuhe. Jede Eingabe, die mehr als drei Taps braucht, wird übersprungen. Deshalb entwerfen wir zuerst die Handy-Ansicht und erst danach das Büro. Große Schaltflächen, wenige Pflichtfelder, offline nutzbar, wenn im Keller kein Netz ist – und die Daten gehen raus, sobald wieder Empfang da ist. Das klingt banal, entscheidet aber darüber, ob ein System nach drei Wochen noch benutzt wird oder nicht.",
      },
      {
        heading: "Was sich konkret rechnet",
        body:
          "Rechnen Sie ehrlich nach: Wenn Sie pro Woche fünf Stunden mit Angeboten, Stundenzetteln und Suchen verbringen, sind das im Jahr rund 250 Stunden. Zum Stundensatz eines Facharbeiters ist das ein deutlich fünfstelliger Betrag – Jahr für Jahr. Dazu kommen die Stunden, die nie verrechnet wurden, weil sie niemand notiert hat. Genau hier setzen wir an: Wir automatisieren zuerst das, was am meisten Zeit kostet, und liefern in Etappen. Sie sehen den Effekt nach wenigen Wochen, nicht nach einem Jahr Projektlaufzeit.",
      },
      {
        heading: "Förderungen für Digitalisierung im Handwerk",
        body:
          "Für Digitalisierungsvorhaben gibt es in Österreich regelmäßig Förderprogramme – über die Wirtschaftskammer, das AWS oder Landesförderungen, je nach Bundesland und Zeitpunkt. Wir sind keine Förderberatung, weisen Sie aber auf mögliche Programme hin und planen das Projekt so, dass es zu den üblichen Anforderungen passt: klar abgegrenzter Umfang, nachvollziehbare Angebote, saubere Dokumentation. Was aktuell verfügbar ist, prüfen Sie am besten direkt bei Ihrer Wirtschaftskammer – wir liefern die Unterlagen, die Sie dafür brauchen.",
      },
    ],
    deliverables: [
      "Angebots- & Auftragsverwaltung mit eigenem Leistungskatalog",
      "Mobile Zeiterfassung für Monteure (auch offline)",
      "Foto-Dokumentation direkt am Auftrag",
      "Material- & Bestellübersicht pro Baustelle",
      "Übergabe an Ihre Buchhaltung oder Ihren Steuerberater",
    ],
    example: {
      title: "Vom Zettel im Auto zum Angebot beim Kaffee",
      persona: "Typisches Szenario: Elektrobetrieb, 12 Mitarbeiter",
      problem:
        "Aufträge lagen in Excel, WhatsApp und auf Zetteln. Der Chef schrieb Angebote abends um 21 Uhr, Stundenzettel kamen zerknittert und unvollständig zurück.",
      solution:
        "Ein zentrales Auftragssystem mit eigenem Leistungskatalog, mobiler Zeiterfassung für die Monteure und Foto-Doku pro Baustelle. Angebote entstehen direkt beim Kunden.",
      result:
        "Angebote gehen am selben Tag raus statt nach einer Woche. Verrechenbare Stunden, die früher untergingen, landen jetzt auf der Rechnung.",
    },
    faq: [
      {
        q: "Meine Monteure sind nicht technikaffin – funktioniert das trotzdem?",
        a: "Genau dafür bauen wir es. Die Monteure-Ansicht besteht aus wenigen großen Schaltflächen: Baustelle wählen, Start, Stopp, Foto. Wer WhatsApp bedienen kann, kann das auch. Wir schulen Ihr Team persönlich und sind in den ersten Wochen erreichbar, wenn etwas hakt.",
      },
      {
        q: "Ich habe schon ein Handwerkerprogramm. Lohnt sich ein Wechsel?",
        a: "Nicht zwingend. Oft ist es sinnvoller, das bestehende Programm zu behalten und nur die Lücken zu schließen – etwa eine mobile Zeiterfassung, die per Schnittstelle Daten liefert. Im Erstgespräch sagen wir Ihnen ehrlich, was der bessere Weg ist.",
      },
      {
        q: "Was kostet so ein System für einen Betrieb meiner Größe?",
        a: "Das hängt davon ab, wie viele Abläufe abgebildet werden. Wir starten bewusst klein mit dem, was am meisten Zeit spart, und bauen aus, wenn sich der erste Schritt bezahlt gemacht hat. Im kostenlosen Erstgespräch bekommen Sie einen konkreten Fixpreis-Rahmen.",
      },
      {
        q: "Was passiert mit meinen Daten, wenn wir nicht mehr zusammenarbeiten?",
        a: "Sie bekommen den vollständigen Quellcode und einen Export Ihrer Daten. Sie sind zu keinem Zeitpunkt bei uns eingesperrt – das ist uns wichtig und steht so auch im Vertrag.",
      },
    ],
    relatedServices: ["softwareentwicklung", "kuenstliche-intelligenz", "suchmaschinenoptimierung-seo"],
    relatedSlugs: ["angebots-software", "zeiterfassung", "excel-abloesen"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "gastronomie-hotellerie",
    kundenHinweis:
      "Gäste suchen „Restaurant Weihnachtsfeier Wien“ oder „Brunch in der Nähe“ – wir schalten Anzeigen für Feiern, Gruppen und Catering und messen sie an echten Reservierungen.",
    icon: "utensils",
    title: "Gastronomie & Hotellerie",
    navTitle: "Gastronomie",
    belege: ["pizza-schwarzlackenau", "smash-me"],
    tagline: "Mehr Gäste, weniger Telefon – digital für Restaurant und Hotel.",
    summary:
      "Reservierung, Dienstplan, Speisekarte und Sichtbarkeit bei Google: Wir digitalisieren genau die Abläufe, die im Servicealltag am meisten Nerven kosten.",
    keywords: [
      "Software für Gastronomie",
      "Reservierungssystem Restaurant",
      "Dienstplan Gastronomie Software",
      "Website Restaurant Wien",
      "Digitalisierung Hotellerie Österreich",
    ],
    metaTitle: "Software für Gastronomie & Hotellerie",
    metaDescription:
      "Reservierung ohne Provision, Dienstplan, digitale Speisekarte, mehr Sichtbarkeit bei Google. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Für Gastronomie & Hotellerie",
    heroHeadline: "Ihr Platz ist im Gastraum – nicht am Telefon und im Dienstplan-Chaos",
    heroSub:
      "Reservierungen per Telefon, Dienstpläne in WhatsApp, eine Speisekarte, die online nie stimmt: Wir bringen das in ein System, das Ihr Team von selbst benutzt – und das keine Provision pro Gast verlangt.",
    pains: [
      {
        title: "Portale kassieren bei jedem Gast mit",
        text: "Die großen Reservierungsplattformen bringen Gäste – und nehmen pro Kopf oder pauschal monatlich mit. Bei Ihren Margen ist das der Unterschied zwischen Gewinn und Nullrunde.",
      },
      {
        title: "Das Telefon klingelt zur schlechtesten Zeit",
        text: "Mitten im Mittagsgeschäft ruft jemand für Freitagabend an. Jemand muss ans Telefon, jemand muss ins Buch schauen – und der Gast am Tisch wartet.",
      },
      {
        title: "Dienstplan-Chaos in der WhatsApp-Gruppe",
        text: "Wer tauscht mit wem? Wer hat den Plan überhaupt gesehen? Jede Änderung löst eine Nachrichtenlawine aus, und am Ende fehlt trotzdem jemand.",
      },
      {
        title: "Online stimmt nichts mehr",
        text: "Die Speisekarte auf der Website ist von 2023, die Öffnungszeiten bei Google sind falsch, und das Feiertagsmenü steht nirgends. Gäste kommen umsonst – oder gar nicht.",
      },
    ],
    solutions: [
      {
        title: "Eigenes Reservierungssystem, keine Provision",
        text: "Gäste buchen direkt auf Ihrer Website. Sie legen Kapazitäten, Zeitfenster und Sperrtage fest. Bestätigung und Erinnerung gehen automatisch raus – und der Gast gehört Ihnen, nicht dem Portal.",
      },
      {
        title: "Dienstplan, den alle sehen",
        text: "Ein Plan, eine Wahrheit. Wünsche werden eingetragen, Tauschanfragen laufen über das System, jede Änderung erreicht sofort alle Betroffenen.",
      },
      {
        title: "Einmal pflegen, überall aktuell",
        text: "Sie ändern die Speisekarte an einer Stelle – Website, Aushang-PDF und QR-Code-Karte am Tisch aktualisieren sich mit.",
      },
    ],
    sections: [
      {
        heading: "Was Reservierungsportale wirklich kosten",
        body:
          "Reservierungsplattformen sind bequem und bringen anfangs zusätzliche Gäste. Der Preis zeigt sich erst später: laufende Gebühren pro Gast oder pro Monat, wenig Kontrolle über die Darstellung – und vor allem gehört die Gästebeziehung der Plattform, nicht Ihnen. Wer über ein Portal bucht, bekommt dessen Newsletter, nicht Ihren. Ein eigenes Reservierungssystem kostet einmalig statt dauerhaft und bringt Ihnen die Gästedaten zurück, mit denen Sie Stammgäste aufbauen können. In den meisten Fällen rechnet sich das nach überschaubarer Zeit. Sinnvoll ist oft der Mittelweg: Portale als zusätzlichen Kanal behalten, aber die eigene Website so gut machen, dass ein wachsender Anteil direkt bucht.",
      },
      {
        heading: "Lokale Sichtbarkeit entscheidet über volle Tische",
        body:
          "Der typische Gast sucht nicht nach Ihrem Namen, sondern nach „italienisch essen 1070 Wien\" oder „Restaurant mit Schanigarten in der Nähe\" – oft unterwegs am Handy, kurz vor der Entscheidung. Wer in diesem Moment nicht auftaucht, existiert nicht. Entscheidend sind ein sauber gepflegtes Google-Unternehmensprofil, korrekte und einheitliche Angaben zu Öffnungszeiten und Adresse, echte Fotos und eine schnelle mobile Website. Dazu kommen strukturierte Daten, die Google Speisekarte, Öffnungszeiten und Bewertungen maschinenlesbar liefern. Diese Basis ist bei den meisten Betrieben lückenhaft – und genau deshalb dort am schnellsten zu verbessern.",
      },
      {
        heading: "Personalplanung, die Fluktuation senkt",
        body:
          "Personal ist in der Gastronomie der knappste Faktor. Ein Dienstplan, der zwei Wochen im Voraus steht, Urlaubswünsche berücksichtigt und Tausch ohne Chef-Beteiligung ermöglicht, ist kein Komfort-Feature – er ist ein Argument im Bewerbungsgespräch. Dazu kommt die Auswertung: Wenn Sie sehen, wie sich Personalkosten zu Umsatz pro Schicht verhalten, planen Sie Dienste nach Zahlen statt nach Gefühl. Das ist oft der Punkt, an dem sich ein solches System innerhalb einer Saison bezahlt macht.",
      },
    ],
    deliverables: [
      "Reservierungssystem ohne Provision, direkt auf Ihrer Website",
      "Dienstplanung mit Tauschanfragen & Urlaubswünschen",
      "Digitale Speisekarte mit QR-Code, zentral gepflegt",
      "Google-Unternehmensprofil & lokale Sichtbarkeit",
      "Schnelle, mobile Website mit echten Fotos",
    ],
    example: {
      title: "Direkt gebucht statt teuer vermittelt",
      persona: "Typisches Szenario: Restaurant mit 80 Plätzen",
      problem:
        "Reservierungen liefen über Telefon und ein Portal mit laufenden Gebühren. Das Buch am Eingang war die einzige Wahrheit, und im Mittagsgeschäft ging niemand ans Telefon.",
      solution:
        "Ein eigenes Reservierungssystem auf der Website mit automatischer Bestätigung und Erinnerung, dazu ein aufgeräumtes Google-Profil und eine schnelle mobile Seite.",
      result:
        "Ein wachsender Teil der Gäste bucht direkt, ohne Provision. Das Telefon klingelt seltener, No-Shows gehen durch die Erinnerungsmail zurück.",
    },
    faq: [
      {
        q: "Muss ich die Reservierungsportale dann kündigen?",
        a: "Nein, und meistens raten wir davon auch ab. Portale bringen Reichweite bei Gästen, die Sie noch nicht kennen. Das Ziel ist, den Anteil der Direktbuchungen zu erhöhen – jede Buchung über Ihre eigene Seite spart die Gebühr und bringt Ihnen den Gastkontakt.",
      },
      {
        q: "Wir haben eine Kassensoftware. Passt das zusammen?",
        a: "In vielen Fällen ja. Etliche Kassensysteme bieten Schnittstellen, über die wir Umsätze oder Tischdaten anbinden können. Was möglich ist, prüfen wir vorab konkret an Ihrem System – ohne Versprechungen ins Blaue.",
      },
      {
        q: "Wie lange dauert die Umstellung mitten im Betrieb?",
        a: "Wir stellen nie alles auf einmal um. Üblicherweise startet die Reservierung zuerst und läuft ein paar Wochen parallel zum bisherigen Weg, bis das Team sicher ist. Erst dann kommt der nächste Baustein.",
      },
    ],
    relatedServices: ["softwareentwicklung", "suchmaschinenoptimierung-seo", "google-ads"],
    relatedSlugs: ["terminbuchungssystem", "ki-chatbot-website"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "arztpraxen-therapeuten",
    icon: "stethoscope",
    title: "Arztpraxen & Therapeuten",
    navTitle: "Arztpraxen",
    belege: ["heimlichgut"],
    tagline: "Weniger Telefon, weniger Leerläufe – digital in der Praxis.",
    summary:
      "Online-Terminvergabe, automatische Erinnerungen und eine Website, die Patienten finden: DSGVO-konform und ohne Umbau Ihrer bestehenden Praxissoftware.",
    keywords: [
      "Online Terminbuchung Arztpraxis",
      "Software für Arztpraxen Österreich",
      "Website Zahnarzt Wien",
      "Terminerinnerung Patienten",
      "Digitalisierung Physiotherapie",
    ],
    metaTitle: "Online-Termine für Arztpraxen",
    metaDescription:
      "Weniger Telefon, weniger Ausfälle: Online-Terminvergabe, Erinnerungen, digitale Anamnese. DSGVO-konform. Jetzt kostenloses Erstgespräch.",
    heroEyebrow: "Für Arztpraxen & Therapeuten",
    heroHeadline: "Ihre Ordination soll behandeln – nicht den ganzen Tag telefonieren",
    heroSub:
      "Das Telefon läuft heiß, Patienten erscheinen nicht, und die Rezeption arbeitet zwei Stunden am Tag nur an Terminen. Wir digitalisieren genau diesen Teil – DSGVO-konform und ohne dass Sie Ihre Praxissoftware anfassen müssen.",
    pains: [
      {
        title: "Das Telefon blockiert die Rezeption",
        text: "Jeden Vormittag dieselbe Welle: Termin verschieben, Termin absagen, Termin ausmachen. Wer in der Praxis steht, wartet – und wer anruft, kommt nicht durch.",
      },
      {
        title: "Nicht erschienene Patienten kosten bares Geld",
        text: "Ein vergessener Termin ist ein leerer Behandlungsstuhl. Bei mehreren pro Woche summiert sich das über das Jahr zu einem ernsthaften Betrag.",
      },
      {
        title: "Patienten finden Sie online nicht",
        text: "Wer nach einer Ordination im Bezirk sucht, landet bei Bewertungsportalen statt bei Ihnen. Ohne eigene Sichtbarkeit bestimmen andere, wie Sie dargestellt werden.",
      },
      {
        title: "Formulare auf Papier, dreimal abgetippt",
        text: "Anamnesebogen ausdrucken, ausfüllen lassen, abtippen, ablegen. Jeder Schritt kostet Zeit und ist eine Fehlerquelle – und datenschutzrechtlich ist der Stapel an der Rezeption auch nicht ideal.",
      },
    ],
    solutions: [
      {
        title: "Termine rund um die Uhr buchbar",
        text: "Patienten buchen selbst, aber nur in den Fenstern und Terminarten, die Sie freigeben. Erstgespräch 30 Minuten, Kontrolle 15 – Sie bestimmen die Regeln, das System hält sie ein.",
      },
      {
        title: "Erinnerungen, die Ausfälle senken",
        text: "Automatische Erinnerung per E-Mail oder SMS, mit einem Klick zum Absagen. Ein abgesagter Termin ist besser als ein leerer Stuhl – und die Lücke wird nachbesetzbar.",
      },
      {
        title: "Formulare digital, vor dem Termin",
        text: "Der Anamnesebogen kommt ausgefüllt an, bevor der Patient da ist. Verschlüsselt übertragen, auf Servern in der EU, ohne Papierstapel an der Rezeption.",
      },
    ],
    sections: [
      {
        heading: "Datenschutz ist bei Gesundheitsdaten keine Formalie",
        body:
          "Gesundheitsdaten gehören zu den besonderen Kategorien personenbezogener Daten nach Artikel 9 DSGVO – dafür gelten strengere Regeln als für gewöhnliche Kontaktdaten. Praktisch heißt das: Verarbeitung auf Servern innerhalb der EU, Verschlüsselung bei Übertragung und Speicherung, ein Berechtigungskonzept, das genau festlegt, wer was sehen darf, ein Auftragsverarbeitungsvertrag und nachvollziehbare Löschfristen. Wir bauen das von Anfang an ein, statt es nachzurüsten. Wichtig zur Klarstellung: Wir liefern die technische Umsetzung und dokumentieren sie sauber – die datenschutzrechtliche Bewertung für Ihre Ordination bleibt bei Ihnen und Ihrem Datenschutzbeauftragten. Wir liefern die Unterlagen, die diese Bewertung möglich machen.",
      },
      {
        heading: "Was Terminausfälle tatsächlich kosten",
        body:
          "Rechnen Sie es für Ihre Ordination durch: Wenn pro Woche drei Termine ersatzlos ausfallen und jeder Behandlungsplatz eine spürbare Stundenleistung erwirtschaftet, ist das über ein Jahr ein erheblicher Betrag – ohne dass Sie mehr arbeiten müssten, um ihn zu heben. Erinnerungen ein bis zwei Tage vor dem Termin reduzieren Ausfälle in der Praxis deutlich, weil die meisten Patienten den Termin schlicht vergessen haben und nicht bewusst fernbleiben. Noch wirksamer wird es, wenn eine Absage automatisch eine Warteliste anschreibt und die Lücke von selbst schließt.",
      },
      {
        heading: "Gefunden werden, ohne Bewertungsportale",
        body:
          "Ein großer Teil der Patientensuche beginnt bei Google – meist mit Fachrichtung und Bezirk, etwa „Zahnarzt 1090 Wien\" oder „Physiotherapie in der Nähe mit Kassenvertrag\". Wer hier nicht auftaucht, überlässt das Feld den Portalen, die Ihre Praxis darstellen, wie es ihnen passt. Die Grundlagen sind überschaubar: eine schnelle, mobile Website, klare Angaben zu Kassenverträgen und Öffnungszeiten, ein gepflegtes Google-Unternehmensprofil, strukturierte Daten für Ordinationszeiten und je eine eigene Seite pro Behandlungsschwerpunkt. Letzteres ist der stärkste Hebel: Wer nach einer konkreten Behandlung sucht, findet dann Ihre Seite statt einer allgemeinen Startseite.",
      },
    ],
    deliverables: [
      "Online-Terminvergabe mit Ihren Regeln & Terminarten",
      "Automatische Erinnerungen per E-Mail oder SMS",
      "Digitale Anamnese- & Aufnahmeformulare",
      "Website mit eigener Seite je Behandlungsschwerpunkt",
      "Wartelisten-Funktion zum Nachbesetzen von Absagen",
    ],
    example: {
      title: "Zwei Stunden Telefon weniger – pro Tag",
      persona: "Typisches Szenario: Zahnarztpraxis mit drei Behandlungsräumen",
      problem:
        "Die Rezeption verbrachte den Vormittag am Telefon. Terminabsagen kamen zu spät oder gar nicht, Anamnesebögen wurden von Hand abgetippt.",
      solution:
        "Online-Terminvergabe mit klar definierten Terminarten, automatische Erinnerung zwei Tage vorher und digitale Anamnese vor dem Besuch.",
      result:
        "Ein großer Teil der Terminvereinbarungen läuft ohne Telefon. Kurzfristige Absagen werden über die Warteliste nachbesetzt statt zu verfallen.",
    },
    faq: [
      {
        q: "Ist eine Online-Terminbuchung mit der DSGVO vereinbar?",
        a: "Ja, wenn sie richtig gebaut ist: Server in der EU, verschlüsselte Übertragung, klare Löschfristen, Auftragsverarbeitungsvertrag und ein sauberes Berechtigungskonzept. Wir setzen das technisch um und dokumentieren es so, dass Ihr Datenschutzbeauftragter es bewerten kann.",
      },
      {
        q: "Ersetzt das unsere Praxissoftware?",
        a: "Nein, und das ist auch nicht das Ziel. Ihre Praxissoftware bleibt das führende System. Wir ergänzen sie um den Teil, der Patienten betrifft – Termine, Erinnerungen, Formulare – und binden sie an, wenn eine Schnittstelle existiert.",
      },
      {
        q: "Was, wenn Patienten unpassende Termine buchen?",
        a: "Das verhindern die Regeln, die Sie festlegen: Welche Terminart wie lange dauert, welche Zeitfenster überhaupt buchbar sind, wie viel Vorlauf nötig ist und welche Termine grundsätzlich nur telefonisch vergeben werden. Das System lässt nur zu, was Sie erlaubt haben.",
      },
    ],
    relatedServices: ["softwareentwicklung", "suchmaschinenoptimierung-seo"],
    relatedSlugs: ["terminbuchungssystem", "kundenportal"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "steuerberater-kanzleien",
    icon: "calculator",
    title: "Steuerberater & Kanzleien",
    navTitle: "Steuerberater",
    tagline: "Belege ohne Nachlauf – Mandantenportal statt E-Mail-Pingpong.",
    summary:
      "Ein sicheres Portal, über das Mandanten Unterlagen selbst hochladen, plus automatische Erinnerungen und Dokumentenerkennung. Weniger Nachlaufen, mehr Beratung.",
    keywords: [
      "Mandantenportal Steuerberater",
      "Software für Steuerkanzlei",
      "Belege digital erfassen Kanzlei",
      "Digitalisierung Steuerberatung Österreich",
      "sicherer Dokumentenaustausch Mandanten",
    ],
    metaTitle: "Mandantenportal für Steuerberater",
    metaDescription:
      "Belege ohne Nachlaufen: sicheres Portal, automatische Erinnerungen, KI-Belegerkennung. Jetzt kostenloses Erstgespräch für Ihre Kanzlei.",
    heroEyebrow: "Für Steuerberater & Kanzleien",
    heroHeadline: "Sie beraten – statt Belegen hinterherzutelefonieren",
    heroSub:
      "Unterlagen kommen zu spät, unvollständig und über fünf verschiedene Kanäle. Wir bauen Ihnen ein Portal, in dem Mandanten selbst hochladen, das automatisch erinnert und Belege bereits vorsortiert.",
    pains: [
      {
        title: "Belege kommen über fünf Kanäle",
        text: "Ein Teil per E-Mail, ein Teil per WhatsApp-Foto, der Rest im Schuhkarton. Jeder Kanal braucht eine andere Behandlung, und nichts ist an einem Ort.",
      },
      {
        title: "Nachlaufen frisst die Fristen auf",
        text: "Ihr Team erinnert dieselben Mandanten jeden Monat aufs Neue. Das ist qualifizierte Arbeitszeit, die für Erinnerungs-E-Mails draufgeht statt für Beratung.",
      },
      {
        title: "E-Mail ist der falsche Ort für Steuerdaten",
        text: "Jahresabschlüsse und Lohndaten wandern unverschlüsselt durch Postfächer. Das ist bequem – und bei sensiblen Mandantendaten ein Risiko, das sich vermeiden lässt.",
      },
      {
        title: "Mandanten fragen nach dem Stand",
        text: "„Ist meine Erklärung schon raus?\" Jede dieser Fragen unterbricht jemanden bei der Arbeit – obwohl die Antwort im System längst steht.",
      },
    ],
    solutions: [
      {
        title: "Ein Portal statt fünf Kanäle",
        text: "Mandanten laden Unterlagen selbst hoch – vom Handy oder vom Rechner, jederzeit. Alles landet automatisch beim richtigen Mandanten und im richtigen Zeitraum.",
      },
      {
        title: "Erinnerungen laufen von selbst",
        text: "Das System sieht, was fehlt, und erinnert automatisch. Ihr Team greift nur noch bei den Fällen ein, die wirklich Aufmerksamkeit brauchen.",
      },
      {
        title: "Belege vorsortiert statt roh",
        text: "Beträge, Datum und Belegart werden automatisch ausgelesen und vorgeschlagen. Ihr Team prüft und bestätigt, statt alles von Hand zu erfassen.",
      },
    ],
    sections: [
      {
        heading: "Warum E-Mail für Mandantenunterlagen der falsche Weg ist",
        body:
          "E-Mail ist bequem und deshalb überall im Einsatz – aber sie ist für sensible Daten schlecht geeignet. Anhänge liegen unverschlüsselt in Postfächern, werden weitergeleitet, landen in privaten Accounts und lassen sich nachträglich kaum kontrollieren. Bei Steuer-, Lohn- und Jahresabschlussdaten ist das ein reales Risiko, sowohl datenschutzrechtlich als auch für das Vertrauensverhältnis zum Mandanten. Ein Portal dreht das um: Zugriff nur nach Anmeldung, jede Datei einem Mandat zugeordnet, jeder Zugriff protokolliert, Löschfristen automatisch durchgesetzt. Für den Mandanten ändert sich wenig – er lädt hoch statt anzuhängen. Für die Kanzlei ändert sich viel: Es gibt einen Ort, an dem alles liegt, und einen nachvollziehbaren Stand.",
      },
      {
        heading: "Was Belegerkennung heute leisten kann – und was nicht",
        body:
          "Moderne Dokumentenerkennung liest aus einem fotografierten Beleg zuverlässig Datum, Betrag, Steuersatz und Lieferant aus, auch bei schiefen Handyfotos und ungewohnten Layouts. Was sie nicht leisten kann und auch nicht soll, ist die fachliche Entscheidung: Kontierung, steuerliche Würdigung und Zuordnung im Grenzfall bleiben bei Ihnen. Der richtige Einsatz ist deshalb nicht Automatisierung um jeden Preis, sondern Vorarbeit: Das System schlägt vor, Ihr Team bestätigt oder korrigiert. Genau in dieser Aufteilung liegt der Zeitgewinn – die stumpfe Erfassung fällt weg, die fachliche Kontrolle bleibt.",
      },
      {
        heading: "Mandantenbindung durch Transparenz",
        body:
          "Kanzleien konkurrieren längst nicht mehr nur über fachliche Qualität – die wird vorausgesetzt. Der spürbare Unterschied im Alltag ist, wie einfach die Zusammenarbeit ist. Ein Mandant, der jederzeit sieht, welche Unterlagen fehlen, wo seine Erklärung steht und was als Nächstes ansteht, ruft nicht an und wechselt seltener. Für Ihre Kanzlei ist dieselbe Transparenz nach innen wertvoll: Sie sehen auf einen Blick, welche Mandate im Rückstand sind, wo Fristen drücken und wie die Auslastung im Team verteilt ist – statt das aus Erfahrung und Bauchgefühl zusammenzusetzen.",
      },
    ],
    deliverables: [
      "Sicheres Mandantenportal mit Zwei-Faktor-Anmeldung",
      "Automatische Erinnerungen für fehlende Unterlagen",
      "KI-gestützte Beleg- & Rechnungserkennung",
      "Status-Übersicht für Mandanten & Kanzlei",
      "Fristen- & Aufgabenübersicht für das Team",
    ],
    example: {
      title: "Der Monatsabschluss ohne Nachlauf",
      persona: "Typisches Szenario: Steuerkanzlei mit 8 Mitarbeitern",
      problem:
        "Belege kamen per E-Mail, WhatsApp und Papier. Zwei Mitarbeiter verbrachten jeden Monatsanfang damit, Mandanten zu erinnern und Unterlagen zu sortieren.",
      solution:
        "Ein Mandantenportal mit Upload per Handy, automatischen Erinnerungen bei fehlenden Unterlagen und Belegerkennung, die Datum, Betrag und Lieferant vorschlägt.",
      result:
        "Unterlagen kommen früher und vollständiger. Die Zeit, die vorher ins Erinnern ging, fließt in Beratung – die Leistung, die tatsächlich Honorar bringt.",
    },
    faq: [
      {
        q: "Wir arbeiten mit BMD, RZL oder DATEV. Ersetzt das unsere Software?",
        a: "Nein. Ihre Kanzleisoftware bleibt das führende System. Das Portal sitzt davor und regelt den Weg vom Mandanten zu Ihnen. Wo eine Schnittstelle existiert, binden wir sie an; wo nicht, arbeiten wir mit sauberen Exportformaten.",
      },
      {
        q: "Sind unsere Mandanten dafür technisch bereit?",
        a: "Erfahrungsgemäß mehr, als man erwartet – wenn das Portal einfach genug ist. Beleg fotografieren und hochladen ist für die meisten leichter als eine E-Mail mit Anhang. Für die wenigen, die nicht mitgehen, bleibt der bisherige Weg offen.",
      },
      {
        q: "Wo liegen die Daten?",
        a: "Auf Servern innerhalb der EU, verschlüsselt, mit protokolliertem Zugriff. Auf Wunsch auch in Österreich oder in Ihrer eigenen Infrastruktur – gerade bei Kanzleien fragen wir das im Erstgespräch aktiv ab.",
      },
    ],
    relatedServices: ["softwareentwicklung", "kuenstliche-intelligenz", "it-consulting"],
    relatedSlugs: ["kundenportal", "dokumenten-automatisierung"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "transport-logistik",
    icon: "truck",
    title: "Transport & Logistik",
    navTitle: "Transport & Logistik",
    belege: ["umzug-alpenland"],
    tagline: "Vom Auftrag bis zum Ablieferbeleg – alles digital nachvollziehbar.",
    summary:
      "Disposition, Fahrer-App, digitale Ablieferbelege und Fuhrparkverwaltung: für Speditionen, Transportunternehmen und Lieferdienste, die den Überblick behalten wollen.",
    keywords: [
      "Software für Speditionen",
      "Transportmanagement Software Österreich",
      "Fahrer App Auftragsverwaltung",
      "digitaler Ablieferbeleg",
      "Fuhrparkverwaltung Software",
    ],
    metaTitle: "Software für Transport & Logistik",
    metaDescription:
      "Disposition in Echtzeit, Fahrer-App mit digitalem Ablieferbeleg, Fristenüberwachung. Jetzt kostenloses Erstgespräch für Ihren Fuhrpark.",
    heroEyebrow: "Für Transport & Logistik",
    heroHeadline: "Wo ist die Ware? Diese Frage sollte niemand mehr telefonisch klären müssen",
    heroSub:
      "Disposition per Telefon, Ablieferbelege auf Papier, Nachweise, die drei Wochen später auftauchen: Wir bauen ein System, in dem jeder Auftrag von der Annahme bis zum unterschriebenen Beleg nachvollziehbar ist.",
    pains: [
      {
        title: "Disposition läuft über Zuruf und Telefon",
        text: "Der Disponent hat den Plan im Kopf und am Whiteboard. Fällt er aus, steht der Betrieb – und jede Änderung bedeutet zehn Anrufe.",
      },
      {
        title: "Ablieferbelege verschwinden",
        text: "Der unterschriebene Lieferschein liegt im LKW, im Fahrerhaus oder gar nicht mehr. Ohne Nachweis wird die Rechnung reklamiert – und Sie können nichts belegen.",
      },
      {
        title: "Kunden fragen ständig nach dem Status",
        text: "Jeder Anruf „Wo ist meine Sendung?\" unterbricht die Disposition. Die Information ist da, aber sie erreicht den Kunden nicht von selbst.",
      },
      {
        title: "Der Fuhrpark ist eine Blackbox",
        text: "Wann war welches Fahrzeug beim Service? Wann läuft die Prüfung ab? Wer hat wie viel verbraucht? Meist steht das verstreut in Ordnern und Excel-Listen.",
      },
    ],
    solutions: [
      {
        title: "Disposition, die alle sehen",
        text: "Aufträge, Fahrzeuge und Fahrer auf einer Übersicht. Änderungen erreichen den Fahrer sofort in der App, statt über eine Telefonkette zu laufen.",
      },
      {
        title: "Ablieferbeleg digital & sofort",
        text: "Unterschrift auf dem Handy, Foto der Ware, Zeitstempel und Standort. Der Beleg ist in Sekunden im System und beim Kunden – nicht in drei Tagen.",
      },
      {
        title: "Fuhrpark mit Vorwarnung",
        text: "Service-Intervalle, § 57a-Prüfungen, Führerschein- und Fristenüberwachung mit automatischer Erinnerung, bevor etwas abläuft.",
      },
    ],
    sections: [
      {
        heading: "Warum digitale Ablieferbelege sich als Erstes rechnen",
        body:
          "Der Ablieferbeleg ist der Punkt, an dem im Transportgeschäft am meisten Geld verloren geht. Fehlt der Nachweis, wird die Rechnung reklamiert, die Zahlung verzögert sich, und im Streitfall steht Aussage gegen Aussage. Ein digitaler Beleg mit Unterschrift, Zeitstempel, Standort und Foto der Ware kostet den Fahrer zwanzig Sekunden und beendet diese Diskussion, bevor sie beginnt. Zusätzlich verkürzt sich der Weg zur Rechnung erheblich: Statt zu warten, bis der Papierbeleg im Büro ankommt, kann die Fakturierung am selben Tag laufen. Das verbessert die Liquidität spürbar – gerade in einer Branche, in der Zahlungsziele ohnehin lang sind.",
      },
      {
        heading: "Sendungsverfolgung als Verkaufsargument",
        body:
          "Ihre Kunden sind aus dem Privatbereich gewohnt, jede Bestellung minutengenau zu verfolgen. Diese Erwartung bringen sie in den gewerblichen Bereich mit. Ein einfacher Statuslink, den der Kunde selbst aufrufen kann – abgeholt, unterwegs, zugestellt, hier ist der Beleg – kostet Sie nichts an laufender Arbeit und nimmt Ihrer Disposition die Statusanrufe ab. Im Vertrieb ist es außerdem ein Argument, mit dem Sie sich von Mitbewerbern abheben, die noch telefonisch Auskunft geben. Der Aufwand dafür ist gering, weil die Daten ohnehin im System entstehen; sie müssen nur sichtbar gemacht werden.",
      },
      {
        heading: "Fristen, die teuer werden, wenn man sie übersieht",
        body:
          "Im Fuhrpark hängen viele Fristen aneinander: wiederkehrende Begutachtung nach § 57a, Service-Intervalle, Reifenwechsel, Führerscheine, Lenkerqualifikationen, ADR-Bescheinigungen. Jede einzelne ist harmlos, solange sie im Blick ist – und teuer, sobald sie durchrutscht. Ein abgelaufener Nachweis bei einer Kontrolle kostet Strafe und Standzeit, im schlimmsten Fall den Versicherungsschutz. Eine zentrale Fristenübersicht mit automatischer Vorwarnung ist technisch simpel und gehört zu den Bausteinen, die sich am schnellsten bezahlt machen, weil ein einziger vermiedener Vorfall die Investition oft schon deckt.",
      },
    ],
    deliverables: [
      "Auftrags- & Dispositionsübersicht in Echtzeit",
      "Fahrer-App mit digitalem Ablieferbeleg & Foto",
      "Sendungsverfolgung als Link für Ihre Kunden",
      "Fuhrpark- & Fristenverwaltung mit Vorwarnung",
      "Auswertungen zu Auslastung, Touren & Kosten",
    ],
    example: {
      title: "Der Nachweis, der nicht mehr verschwindet",
      persona: "Typisches Szenario: Spedition mit 15 Fahrzeugen",
      problem:
        "Disposition lief über Telefon und Whiteboard, Ablieferbelege kamen auf Papier zurück – manchmal Tage später, manchmal gar nicht. Reklamationen ließen sich nicht widerlegen.",
      solution:
        "Digitale Disposition mit Fahrer-App, Unterschrift und Foto direkt bei der Zustellung, dazu ein Statuslink für Kunden und eine Fristenübersicht für den Fuhrpark.",
      result:
        "Belege sind sofort verfügbar, Rechnungen gehen am selben Tag raus. Statusanrufe gehen deutlich zurück, weil Kunden selbst nachsehen können.",
    },
    faq: [
      {
        q: "Funktioniert die Fahrer-App auch ohne Empfang?",
        a: "Ja. Die App speichert Eingaben lokal und überträgt sie, sobald wieder Netz da ist. Im Tunnel, in der Tiefgarage oder im Funkloch geht nichts verloren – das ist bei Transportsoftware eine Grundanforderung, keine Zusatzfunktion.",
      },
      {
        q: "Wir haben schon ein Telematiksystem im Fahrzeug. Passt das zusammen?",
        a: "Meistens ja. Viele Telematikanbieter stellen Schnittstellen bereit, über die wir Positions- und Fahrzeugdaten übernehmen können. Dann bleibt die Hardware, wie sie ist, und wir ergänzen den Auftrags- und Belegteil.",
      },
      {
        q: "Ist das auch für kleine Fuhrparks sinnvoll?",
        a: "Ab etwa drei bis fünf Fahrzeugen fast immer, weil schon dort niemand mehr alles im Kopf hat. Bei kleineren Betrieben starten wir mit dem digitalen Ablieferbeleg allein – das ist der Baustein mit dem schnellsten Effekt.",
      },
    ],
    relatedServices: ["softwareentwicklung", "it-consulting", "qualitaetssicherung-qa"],
    relatedSlugs: ["mitarbeiter-app", "excel-abloesen"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "einzelhandel-ecommerce",
    icon: "shopping-bag",
    title: "Einzelhandel & E-Commerce",
    navTitle: "Handel & Shop",
    tagline: "Laden und Online als ein Geschäft – nicht als zwei Baustellen.",
    summary:
      "Bestand, der überall stimmt, ein Shop, der gefunden wird, und Prozesse, die nicht an der Ladentür enden. Für Händler mit Geschäft, Shop oder beidem.",
    keywords: [
      "Onlineshop erstellen lassen Österreich",
      "Warenwirtschaft kleine Händler",
      "Lagerbestand synchronisieren Shop",
      "SEO für Onlineshop",
      "Digitalisierung Einzelhandel",
    ],
    metaTitle: "Software für Handel & Onlineshop",
    metaDescription:
      "Ein Bestand für Laden und Shop, Produktseiten die Google versteht, Click & Collect. Jetzt kostenloses Erstgespräch für Ihren Handel.",
    heroEyebrow: "Für Einzelhandel & E-Commerce",
    heroHeadline: "Zwei Verkaufskanäle, eine Wahrheit beim Bestand",
    heroSub:
      "Im Laden verkauft, online noch verfügbar – und schon ist ein Kunde enttäuscht. Wir verbinden Geschäft und Shop so, dass Bestand, Preise und Bestellungen an einer Stelle stimmen.",
    pains: [
      {
        title: "Der Bestand stimmt nie überall",
        text: "Was im Laden über den Tresen geht, steht online noch als verfügbar. Sie stornieren, entschuldigen sich – und der Kunde bestellt beim Nächsten.",
      },
      {
        title: "Der Shop kostet mehr als er bringt",
        text: "Monatliche Gebühren, Transaktionsgebühren, Gebühren für jedes Zusatzmodul. Am Jahresende steht ein Betrag, der zur Umsatzgröße nicht passt.",
      },
      {
        title: "Bei Google unsichtbar",
        text: "Ihre Produkte sind gut, aber niemand findet sie. Die Suchergebnisse gehören den großen Plattformen, weil deren Produktseiten sauber aufgebaut sind und Ihre nicht.",
      },
      {
        title: "Jede Bestellung wird von Hand angefasst",
        text: "Bestellung ausdrucken, im Lager suchen, Lieferschein tippen, Sendungsnummer in eine E-Mail kopieren. Bei zehn Bestellungen geht das. Bei fünfzig nicht mehr.",
      },
    ],
    solutions: [
      {
        title: "Ein Bestand für alle Kanäle",
        text: "Kasse, Shop und Lager greifen auf denselben Bestand zu. Was im Laden verkauft wird, ist online sofort weg – ohne dass jemand etwas nachträgt.",
      },
      {
        title: "Produktseiten, die Google versteht",
        text: "Jedes Produkt mit eigener, sauber strukturierter Seite, Preis- und Verfügbarkeitsdaten maschinenlesbar. Das ist die Grundlage dafür, überhaupt in den Suchergebnissen aufzutauchen.",
      },
      {
        title: "Bestellabwicklung ohne Handarbeit",
        text: "Bestellung kommt rein, Kommissionierliste entsteht, Versandetikett wird erzeugt, Kunde bekommt automatisch die Sendungsnummer. Sie greifen nur bei Ausnahmen ein.",
      },
    ],
    sections: [
      {
        heading: "Shopsystem oder Eigenentwicklung – die ehrliche Antwort",
        body:
          "Für die meisten Händler ist ein etabliertes Shopsystem die richtige Wahl, und wir sagen das auch so. Zahlungsabwicklung, Steuerlogik, Versandintegration und Sicherheit sind dort gelöst, und das nachzubauen wäre teuer ohne Mehrwert. Eine Eigenentwicklung lohnt sich in zwei Fällen: wenn Ihre Produkte eine Logik brauchen, die Standardshops nicht abbilden – Konfiguratoren, Staffelpreise pro Kunde, Mietmodelle, Sonderanfertigungen –, oder wenn die laufenden Gebühren durch Umsatz und Zusatzmodule so hoch geworden sind, dass eine einmalige Investition sich innerhalb weniger Jahre trägt. Meist ist die beste Lösung eine Kombination: Standardshop als Verkaufsfläche, individuelle Software für das, was dahinter passiert.",
      },
      {
        heading: "Warum Produktseiten über Sichtbarkeit entscheiden",
        body:
          "Die meisten Shops kämpfen um allgemeine Suchbegriffe wie „Laufschuhe kaufen\" – gegen Plattformen mit enormer Domain-Autorität. Dieser Kampf ist für einen mittelgroßen Händler nicht zu gewinnen. Gewinnbar ist die konkrete Suche: eine bestimmte Marke, ein bestimmtes Modell, eine bestimmte Größe, dazu regionale Begriffe wie „in Wien kaufen\" oder „heute abholen\". Solche Suchanfragen haben weniger Volumen, aber deutlich höhere Kaufabsicht und viel schwächere Konkurrenz. Voraussetzung ist, dass jedes Produkt eine eigene, schnell ladende Seite mit eigenem Text, echten Fotos und strukturierten Daten für Preis und Verfügbarkeit hat. Genau daran scheitert es bei den meisten kleineren Shops.",
      },
      {
        heading: "Click & Collect als unterschätzter Vorteil",
        body:
          "Als kleinerer Händler können Sie beim Preis und bei der Lieferzeit selten mit den großen Plattformen mithalten. Was Sie haben und die anderen nicht: einen Ort, an dem der Kunde die Ware sofort bekommt und beraten wird. Online reservieren und im Laden abholen verbindet beides – der Kunde weiß vorher, dass der Artikel da ist, und Sie bekommen ihn ins Geschäft, wo erfahrungsgemäß häufig noch etwas dazukommt. Technisch braucht es dafür nur eines, aber das zwingend: einen Bestand, dem man trauen kann. Ohne verlässliche Verfügbarkeitsanzeige schadet Click & Collect mehr, als es nützt.",
      },
    ],
    deliverables: [
      "Onlineshop oder Anbindung Ihres bestehenden Shops",
      "Bestandsabgleich zwischen Kasse, Lager & Shop",
      "Produktseiten mit strukturierten Daten für Google",
      "Automatisierte Bestell- & Versandabwicklung",
      "Click & Collect mit verlässlicher Verfügbarkeit",
    ],
    example: {
      title: "Ein Bestand statt zwei Wahrheiten",
      persona: "Typisches Szenario: Fachhändler mit Geschäft und Onlineshop",
      problem:
        "Der Shopbestand wurde einmal täglich von Hand aktualisiert. Artikel, die im Laden verkauft wurden, blieben online sichtbar – mit Stornierungen und verärgerten Kunden als Folge.",
      solution:
        "Kasse und Shop wurden an einen gemeinsamen Bestand angebunden. Produktseiten bekamen eigene Texte und strukturierte Daten, dazu kam Click & Collect.",
      result:
        "Stornierungen wegen fehlender Ware entfallen weitgehend. Kunden, die online reservieren, kommen ins Geschäft – und kaufen dort oft mehr als das reservierte Teil.",
    },
    faq: [
      {
        q: "Wir nutzen Shopify oder WooCommerce. Müssen wir wechseln?",
        a: "In der Regel nicht. Beide bieten gute Schnittstellen, über die wir Bestand, Bestellungen und Produktdaten anbinden können. Ein Wechsel lohnt sich nur, wenn das System selbst zum Engpass geworden ist – das sagen wir Ihnen offen.",
      },
      {
        q: "Kann meine bestehende Kassensoftware angebunden werden?",
        a: "Das hängt vom Hersteller ab. Viele moderne Kassensysteme haben eine API. Wir prüfen das konkret an Ihrem System, bevor wir irgendetwas versprechen – wenn es nicht geht, sagen wir das vorher und nicht mittendrin.",
      },
      {
        q: "Lohnt sich SEO für einen kleinen Shop überhaupt?",
        a: "Auf allgemeine Begriffe nicht – gegen die großen Plattformen gewinnen Sie nicht. Auf konkrete Produkt- und Regionalsuchen sehr wohl. Dort ist die Konkurrenz schwach und die Kaufabsicht hoch. Das ist die einzige SEO-Strategie, die für kleinere Shops realistisch funktioniert.",
      },
    ],
    relatedServices: ["softwareentwicklung", "suchmaschinenoptimierung-seo", "google-ads"],
    relatedSlugs: ["excel-abloesen", "ki-chatbot-website"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "immobilien-hausverwaltung",
    icon: "building",
    title: "Immobilien & Hausverwaltung",
    navTitle: "Immobilien",
    tagline: "Objekte, Mieter und Anfragen an einem Ort statt in zehn Ordnern.",
    summary:
      "Für Makler und Hausverwaltungen: Objektverwaltung, Mieterportal, digitale Schadensmeldung und Anfragen, die automatisch beim richtigen Objekt landen.",
    keywords: [
      "Software für Hausverwaltung",
      "Maklersoftware Österreich",
      "Mieterportal Software",
      "digitale Schadensmeldung Mieter",
      "Objektverwaltung Programm",
    ],
    metaTitle: "Software für Hausverwaltung & Makler",
    metaDescription:
      "Mieterportal mit digitaler Schadensmeldung, Objektakte, Anfragen aus allen Portalen an einem Ort. Jetzt kostenloses Erstgespräch.",
    heroEyebrow: "Für Immobilien & Hausverwaltung",
    heroHeadline: "Jede Anfrage beantwortet, jeder Schaden dokumentiert – ohne Ordnerchaos",
    heroSub:
      "Anfragen kommen über sechs Portale, Schadensmeldungen per Anruf, Unterlagen liegen in Ordnern auf drei Rechnern. Wir bringen das zusammen – und geben Mietern und Eigentümern einen Ort, an dem sie selbst nachsehen können.",
    pains: [
      {
        title: "Anfragen versickern zwischen den Portalen",
        text: "Sechs Plattformen, sechs Postfächer. Wer zuerst antwortet, bekommt den Termin – und bei Ihnen liegt die Anfrage noch ungelesen im vierten Tab.",
      },
      {
        title: "Schadensmeldungen kommen per Anruf",
        text: "Der Mieter ruft an, beschreibt einen Wasserfleck, jemand notiert etwas. Ohne Foto, ohne Verlauf, ohne Nachweis, wann was gemeldet wurde.",
      },
      {
        title: "Unterlagen liegen überall und nirgends",
        text: "Der Mietvertrag ist auf dem Laufwerk, die Abrechnung im E-Mail-Anhang, das Protokoll in einem Ordner im Regal. Bei jeder Rückfrage beginnt die Suche von vorn.",
      },
      {
        title: "Eigentümer wollen Zahlen, die Sie erst zusammensuchen",
        text: "Zur Eigentümerversammlung wird aus mehreren Excel-Dateien ein Bericht gebastelt. Jedes Jahr aufs Neue, jedes Jahr unter Zeitdruck.",
      },
    ],
    solutions: [
      {
        title: "Alle Anfragen in einem Posteingang",
        text: "Anfragen aus allen Portalen laufen zusammen, werden automatisch dem richtigen Objekt zugeordnet und bekommen einen Status. Nichts bleibt liegen, weil niemand es gesehen hat.",
      },
      {
        title: "Schadensmeldung mit Foto und Verlauf",
        text: "Der Mieter meldet über das Portal, hängt ein Foto an und sieht den Bearbeitungsstand. Sie haben Zeitpunkt, Beschreibung und Bild dokumentiert – auch für den Versicherungsfall.",
      },
      {
        title: "Ein Ort pro Objekt",
        text: "Verträge, Abrechnungen, Protokolle, Schäden und Ansprechpartner hängen am Objekt. Wer Zugriff hat, findet alles – auch die Kollegin, die das Objekt nicht betreut.",
      },
    ],
    sections: [
      {
        heading: "Reaktionszeit entscheidet über den Abschluss",
        body:
          "Im Maklergeschäft ist Geschwindigkeit oft wichtiger als alles andere. Interessenten fragen selten nur bei einem Objekt an – sie schreiben mehrere gleichzeitig und reagieren auf den, der zuerst antwortet. Wenn Anfragen über verschiedene Portale in verschiedenen Postfächern landen und niemand einen Gesamtüberblick hat, verlieren Sie Abschlüsse an schnellere Mitbewerber, ohne es überhaupt zu merken. Ein gemeinsamer Posteingang mit automatischer Objektzuordnung und einer ersten automatischen Rückmeldung löst genau das. Die automatische Antwort ersetzt keine Beratung, aber sie hält den Interessenten im Gespräch, bis ein Mensch Zeit hat.",
      },
      {
        heading: "Warum Schadensdokumentation Sie schützt",
        body:
          "Ein Wasserschaden, der telefonisch gemeldet und auf einem Notizzettel festgehalten wurde, ist im Streitfall praktisch wertlos. Wann wurde gemeldet? Wie sah es damals aus? Wann wurde reagiert? Ohne Nachweis tragen Sie das Risiko. Eine digitale Schadensmeldung mit Zeitstempel, Foto und lückenlosem Bearbeitungsverlauf kostet den Mieter eine Minute und schützt Verwaltung und Eigentümer gleichermaßen – gegenüber Versicherungen, bei Gewährleistungsfragen und in der Kommunikation mit Handwerkern. Der Nebeneffekt ist mindestens so wertvoll: Mieter, die den Bearbeitungsstand selbst sehen, rufen deutlich seltener an.",
      },
      {
        heading: "Was ein Mieter- und Eigentümerportal wirklich einspart",
        body:
          "Ein großer Teil der Anrufe in einer Hausverwaltung dreht sich um wiederkehrende Fragen: Wie ist der Stand meiner Meldung? Wo finde ich die letzte Abrechnung? Wann ist die nächste Versammlung? Wer ist für mein Objekt zuständig? Jede dieser Fragen ist berechtigt, und jede unterbricht jemanden mitten in der Arbeit. Ein Portal beantwortet sie ohne Ihr Zutun, rund um die Uhr. Für Eigentümer gilt dasselbe eine Ebene höher: aktuelle Zahlen zum Objekt, Belege, Protokolle und Beschlüsse jederzeit einsehbar – statt einmal jährlich mühsam zusammengestellt.",
      },
    ],
    deliverables: [
      "Objektverwaltung mit allen Unterlagen an einem Ort",
      "Gemeinsamer Anfrage-Posteingang über alle Portale",
      "Mieterportal mit digitaler Schadensmeldung",
      "Eigentümerbereich mit Zahlen, Belegen & Protokollen",
      "Termin- & Fristenübersicht je Objekt",
    ],
    example: {
      title: "Vom Anruf zum dokumentierten Vorgang",
      persona: "Typisches Szenario: Hausverwaltung mit 40 Objekten",
      problem:
        "Schadensmeldungen kamen telefonisch und wurden handschriftlich notiert. Mieter riefen mehrfach nach, weil sie den Stand nicht kannten, und Nachweise fehlten im Versicherungsfall.",
      solution:
        "Ein Mieterportal mit Schadensmeldung inklusive Foto und sichtbarem Bearbeitungsstand, dazu eine Objektakte, in der alle Unterlagen und Vorgänge zusammenlaufen.",
      result:
        "Nachfrage-Anrufe gehen deutlich zurück, weil der Status einsehbar ist. Jeder Schaden ist mit Zeitpunkt und Bild dokumentiert – auch Jahre später noch auffindbar.",
    },
    faq: [
      {
        q: "Wir arbeiten mit einer Verwaltungssoftware. Ersetzt das die?",
        a: "Nein. Ihre Verwaltungssoftware bleibt das führende System für Buchhaltung und Abrechnung. Wir ergänzen den Teil, der nach außen wirkt – Portal, Schadensmeldung, Anfragen – und binden an, wo eine Schnittstelle existiert.",
      },
      {
        q: "Nutzen ältere Mieter so ein Portal überhaupt?",
        a: "Nicht alle, und das ist eingeplant. Das Portal ist ein zusätzlicher Weg, kein Ersatz. Telefon und E-Mail bleiben. Erfahrungsgemäß wandert der größere Teil der Meldungen aber schnell ins Portal, weil es für den Mieter der bequemere Weg ist.",
      },
      {
        q: "Können mehrere Mitarbeiter gleichzeitig an einem Objekt arbeiten?",
        a: "Ja. Alles hängt am Objekt, nicht an einer Person. Sie legen fest, wer was sehen und ändern darf. Wenn jemand im Urlaub ist, findet die Vertretung alles am selben Ort.",
      },
    ],
    relatedServices: ["softwareentwicklung", "it-consulting", "kuenstliche-intelligenz"],
    relatedSlugs: ["kundenportal", "excel-abloesen"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "friseur-beauty-studios",
    kundenHinweis:
      "Kundinnen und Kunden suchen „Friseur 1070“ oder „Nageldesign in der Nähe“ – wir schalten Anzeigen im kurzen Radius und messen sie an echten Online-Buchungen.",
    icon: "scissors",
    title: "Friseure, Beauty & Studios",
    navTitle: "Beauty & Studios",
    belege: ["onyx-cuts"],
    tagline: "Der Kalender füllt sich, während Sie arbeiten.",
    summary:
      "Online-Terminbuchung, automatische Erinnerungen und lokale Sichtbarkeit für Friseursalons, Kosmetikstudios, Fitness- und Yogastudios.",
    keywords: [
      "Online Terminbuchung Friseur",
      "Software für Kosmetikstudio",
      "Buchungssystem Fitnessstudio",
      "Website Friseursalon Wien",
      "Terminerinnerung Salon",
    ],
    metaTitle: "Buchungssystem für Friseure & Studios",
    metaDescription:
      "Online buchbar rund um die Uhr, ohne Provision. Erinnerungen senken Ausfälle, lokal besser gefunden. Jetzt kostenloses Erstgespräch.",
    heroEyebrow: "Für Friseure, Beauty & Studios",
    heroHeadline: "Sie stehen am Kunden – der Kalender füllt sich trotzdem",
    heroSub:
      "Während Sie die Hände voll haben, klingelt das Telefon. Wir bauen Ihnen eine Buchung, die rund um die Uhr funktioniert, an Termine erinnert und keine Provision pro Kunde nimmt.",
    pains: [
      {
        title: "Das Telefon klingelt immer im falschen Moment",
        text: "Sie haben Farbe in der Hand oder arbeiten am Kunden. Der Anruf geht ins Leere – und der Kunde ruft beim Salon nebenan an.",
      },
      {
        title: "Nicht erschienene Kunden reißen Löcher",
        text: "Ein vergessener Termin ist eine Stunde ohne Umsatz, die sich nicht nachholen lässt. Bei mehreren pro Woche fehlt am Monatsende ein spürbarer Betrag.",
      },
      {
        title: "Buchungsplattformen kassieren mit",
        text: "Die großen Portale bringen Kunden – und nehmen pro Buchung oder monatlich pauschal. Bei Ihren Preisen ist das ein ernstzunehmender Anteil.",
      },
      {
        title: "Online sind Sie kaum sichtbar",
        text: "Wer im Bezirk nach einem Salon sucht, findet Portale und Mitbewerber. Ihre eigene Seite taucht auf Seite drei auf – wenn überhaupt.",
      },
    ],
    solutions: [
      {
        title: "Buchung rund um die Uhr, ohne Provision",
        text: "Kunden buchen direkt auf Ihrer Seite – abends um elf, wenn ihnen der Termin einfällt. Jede Leistung mit eigener Dauer, jeder Mitarbeiter mit eigenem Kalender.",
      },
      {
        title: "Erinnerung, die Ausfälle spürbar senkt",
        text: "Automatische Erinnerung per SMS oder E-Mail einen Tag vorher, mit Absage-Link. Wer absagt, gibt den Platz frei, statt ihn verfallen zu lassen.",
      },
      {
        title: "Lokal gefunden werden",
        text: "Google-Unternehmensprofil, echte Fotos, eine eigene Seite je Leistung und Bezirk. Damit tauchen Sie dort auf, wo Kunden tatsächlich suchen.",
      },
    ],
    sections: [
      {
        heading: "Warum die meisten Buchungen außerhalb Ihrer Öffnungszeiten entstehen",
        body:
          "Ein erheblicher Teil der Terminanfragen entsteht dann, wenn Ihr Salon geschlossen ist: am späten Abend, am Sonntag, in der Mittagspause des Kunden. Wer in diesem Moment nur eine Telefonnummer findet, verschiebt den Anruf – und vergisst ihn oft. Eine Online-Buchung fängt genau diese Momente ab. Das ist kein Bequemlichkeitsfeature, sondern zusätzlicher Umsatz, der sonst nie entstanden wäre. Wichtig ist dabei, dass die Buchung Ihre Regeln kennt: Welche Leistung dauert wie lange, welche Mitarbeiterin macht was, wie viel Puffer brauchen Sie zwischen zwei Terminen, wie viel Vorlauf muss ein Termin haben. Ein Buchungssystem, das diese Regeln nicht abbildet, macht mehr Arbeit, als es abnimmt.",
      },
      {
        heading: "Terminausfälle systematisch senken",
        body:
          "Die meisten Kunden erscheinen nicht aus Böswilligkeit nicht, sondern weil sie den Termin schlicht vergessen haben. Eine Erinnerung ein bis zwei Tage vorher wirkt deshalb erstaunlich stark – vor allem, wenn sie eine einfache Möglichkeit zum Absagen enthält. Das klingt zunächst widersinnig, ist aber der Kern: Eine Absage 24 Stunden vorher ist wertvoll, weil die Zeit nachbesetzt werden kann. Ein Nichterscheinen ohne Vorwarnung ist verloren. Wer zusätzlich mit einer Warteliste arbeitet, schließt frei werdende Lücken oft noch am selben Tag automatisch.",
      },
      {
        heading: "Lokale Sichtbarkeit ist bei Ihnen fast alles",
        body:
          "Kaum jemand fährt für einen Haarschnitt quer durch die Stadt. Ihre Kunden kommen aus dem Grätzl, und sie suchen entsprechend: „Friseur 1050 Wien\", „Balayage in der Nähe\", „Kosmetikstudio Sonntag offen\". Das bedeutet zweierlei. Erstens ist Ihr Google-Unternehmensprofil wichtiger als Ihre Website – vollständig ausgefüllt, mit echten aktuellen Fotos, korrekten Zeiten und aktiv beantworteten Bewertungen. Zweitens gewinnen Sie mit eigenen Seiten je Leistung mehr als mit einer schönen Startseite. Wer nach einer konkreten Behandlung sucht, soll auf einer Seite landen, die genau davon handelt – mit Preis, Dauer, Bildern und einem Buchungsknopf.",
      },
    ],
    deliverables: [
      "Online-Buchung je Leistung & Mitarbeiter, ohne Provision",
      "Automatische Erinnerung per SMS oder E-Mail",
      "Warteliste zum Nachbesetzen freier Termine",
      "Google-Unternehmensprofil & lokale Sichtbarkeit",
      "Eigene Seite je Leistung mit Preis, Dauer & Fotos",
    ],
    example: {
      title: "Gebucht, während der Salon geschlossen war",
      persona: "Typisches Szenario: Friseursalon mit vier Stühlen",
      problem:
        "Termine liefen ausschließlich über Telefon. Während der Arbeit ging niemand ran, und vergessene Termine rissen jede Woche Löcher in den Kalender.",
      solution:
        "Online-Buchung mit hinterlegten Leistungsdauern und Mitarbeiterkalendern, automatische Erinnerung am Vortag mit Absage-Link, dazu ein aufgeräumtes Google-Profil.",
      result:
        "Ein guter Teil der Termine wird außerhalb der Öffnungszeiten gebucht. Kurzfristige Absagen werden über die Warteliste nachbesetzt statt zu verfallen.",
    },
    faq: [
      {
        q: "Wir nutzen Treatwell oder ein ähnliches Portal. Was ändert sich?",
        a: "Das Portal können Sie behalten – es bringt neue Kunden, die Sie noch nicht kennen. Das Ziel ist, dass Stammkunden über Ihre eigene Seite buchen. Jede Buchung dort spart die Provision und bringt Ihnen den Kundenkontakt.",
      },
      {
        q: "Können unterschiedliche Mitarbeiter unterschiedliche Leistungen anbieten?",
        a: "Ja. Sie legen fest, wer welche Leistung anbietet, wie lange sie dauert und wann die Person arbeitet. Kunden sehen nur, was tatsächlich buchbar ist – dadurch entstehen keine unmöglichen Termine.",
      },
      {
        q: "Was kostet das laufend?",
        a: "Für die Software fallen im Wesentlichen Hosting-Kosten an, plus SMS-Gebühren, wenn Sie Erinnerungen per SMS statt per E-Mail schicken wollen. Es gibt keine Provision pro Buchung – das ist der wesentliche Unterschied zu den Portalen.",
      },
    ],
    relatedServices: ["softwareentwicklung", "suchmaschinenoptimierung-seo", "google-ads"],
    relatedSlugs: ["terminbuchungssystem", "ki-chatbot-website"],
  },

  // ────────────────────────────────────────────────────────────────
  // Maschinenbau und Automotive: Beide stehen in Anlage 2 des NISG 2026
  // (NACE C 28 bzw. C 29). Der Einstieg ist deshalb NIS2, nicht Software –
  // Rechtsangaben geprüft im September 2026 gegen den RIS-Text.
  {
    slug: "maschinenbau",
    icon: "cog",
    title: "Maschinen- und Anlagenbau",
    navTitle: "Maschinenbau",
    tagline: "NIS2, Fernwartung, Maschinensicherheit – prüfbar umgesetzt.",
    summary:
      "Der Maschinenbau steht in Anlage 2 des NISG 2026. Wir setzen die Pflichten mit dem Blick eines Ingenieurs und Experten für Maschinensicherheit um – Büro und Werkshalle, nicht nur das eine.",
    keywords: [
      "nis2 maschinenbau",
      "nisg 2026 maschinenbau",
      "nis2 anlagenbau österreich",
      "fernwartung sicherheit maschinenbau",
      "cybersecurity maschinenbau",
      "nis2 beratung industrie oberösterreich",
    ],
    metaTitle: "NIS2 im Maschinen- und Anlagenbau",
    metaDescription:
      "Maschinenbau fällt unter Anlage 2 des NISG 2026. NIS2-Umsetzung mit Blick auf Fernwartung und Maschinensicherheit – prüfbar dokumentiert, zum Fixpreis.",
    heroEyebrow: "Für Maschinen- und Anlagenbauer",
    heroHeadline: "NIS2 im Maschinenbau: prüfbar umsetzen, ohne die Produktion auszubremsen",
    heroSub:
      "Ab 50 Beschäftigten ist ein Maschinenbauer nach dem NISG 2026 in aller Regel wichtige Einrichtung. Dazu kommen Kundenfragebögen, gewachsene Fernwartungszugänge und ab 2027 die neue Maschinenverordnung. Wir ordnen das, setzen die Maßnahmen um und dokumentieren sie so, dass eine Prüfung sie nachvollziehen kann.",
    pains: [
      {
        title: "Das Büro ist geschützt, die Halle nicht",
        text: "Die Büro-IT hat Backup und Virenschutz. Steuerungen, Prüfstände und Industrie-PCs laufen seit Jahren mit demselben Stand – und niemand hat sie auf der Liste.",
      },
      {
        title: "Fernwartung über zu viele Wege",
        text: "Hersteller, Integratoren und der eigene Service greifen per VPN, Fernwartungssoftware oder Mobilfunk-Router zu. Wer wann auf welche Anlage darf, weiß niemand vollständig.",
      },
      {
        title: "Betroffen und Lieferant zugleich",
        text: "Selbst als wichtige Einrichtung erfasst – und gleichzeitig wollen Kunden wissen, wie Ihr Service-Zugang auf ihre Maschinen abgesichert ist.",
      },
      {
        title: "Vorlagen aus dem Konzern passen nicht",
        text: "Wer Richtlinien für 20.000 Mitarbeitende auf 80 überträgt, hat Papier, das niemand lebt. Das fällt bei der ersten Nachfrage auf.",
      },
    ],
    solutions: [
      {
        title: "Einstufung und Fahrplan",
        text: "Wir prüfen die Einstufung – mit Konzernzurechnung – und machen aus den zehn Maßnahmenbereichen des § 32 einen Plan mit Kosten und Terminen.",
      },
      {
        title: "Fernwartung mit Regeln",
        text: "Ein Zugangsweg statt fünf: Freigabe auf Anfrage, Mehr-Faktor-Anmeldung, persönliche Konten, Protokoll. Abgestimmt mit Ihren Dienstleistern, ohne Produktionsstopp.",
      },
      {
        title: "Nachweise, die tragen",
        text: "Jede Anforderung bekommt eine Maßnahme und einen Nachweis – wie in einer Testmatrix. Daraus beantworten Sie Behörde und Kundenfragebögen aus demselben Bestand.",
      },
    ],
    sections: [
      {
        heading: "Warum der Maschinenbau im NISG 2026 steht",
        body:
          "Anlage 2 des NISG 2026 erfasst Teile des verarbeitenden Gewerbes, darunter ausdrücklich den Maschinenbau – bezeichnet als Abschnitt C, Abteilung 28 der NACE. Maßgeblich ist die Haupttätigkeit. Mittlere Unternehmen ab 50 Beschäftigten oder mit mehr als 10 Mio. € Umsatz und Bilanzsumme sowie große Unternehmen sind wichtige Einrichtungen. Sie müssen sich bis 31. Dezember 2026 registrieren, die Maßnahmen nach § 32 umsetzen, erhebliche Sicherheitsvorfälle melden und bis 30. September 2027 eine Selbstdeklaration abgeben. Wer dagegen hauptsächlich Metall im Lohn bearbeitet, fällt unter Abteilung 25 und ist nicht erfasst – auch wenn die Teile in Maschinen landen. Wo genau die Grenze liegt, ist bei Mischbetrieben nicht immer offensichtlich.",
      },
      {
        heading: "Die Werkshalle gehört dazu",
        body:
          "Die Maßnahmen beziehen sich auf die Netz- und Informationssysteme, die Sie für Ihre Tätigkeit nutzen – nicht nur auf das Büro. Steuerungen, Prüfstände, Industrie-PCs und die Fernwartungszugänge Ihrer Lieferanten zählen dazu. Das heißt nicht, dass jede Anlage umgebaut werden muss. Verlangt sind geeignete und verhältnismäßige Maßnahmen, bei denen auch die Kosten zählen. Es heißt aber: Sie wissen, was es gibt, wie es angebunden ist und was passiert, wenn es ausfällt. Die wirksamsten Schritte sind oft unspektakulär – ein vom Büro getrenntes Produktionsnetz, ein einziger kontrollierter Fernwartungsweg, ein getestetes Backup der Steuerungsprogramme.",
      },
      {
        heading: "Security und Safety zusammen denken",
        body:
          "Wer Maschinen baut, kennt Risikobeurteilung und funktionale Sicherheit. Die EU-Maschinenverordnung, die ab 20. Januar 2027 gilt, verbindet beides: Sicherheitsrelevante Software und Daten müssen gegen unbeabsichtigte und vorsätzliche Manipulation geschützt sein, Steuerungen vorhersehbaren böswilligen Zugriffen standhalten. NIS2 betrifft Ihren Betrieb, die Maschinenverordnung Ihr Produkt – methodisch sind sie eng verwandt. Unser Gründer ist als CMSE Experte für Maschinensicherheit und kommt aus dem Testmanagement regulierter Projekte. Deshalb behandeln wir beides mit derselben Methode: Anforderung, Maßnahme, Nachweis.",
      },
    ],
    deliverables: [
      "Einstufung nach NISG 2026 mit dokumentierter Begründung",
      "Systemübersicht über Büro-IT und Produktion",
      "Fernwartungskonzept mit Freigabeprozess",
      "Maßnahmenplan zu den zehn Bereichen des § 32",
      "Nachweisliste für Kundenfragebögen und Selbstdeklaration",
    ],
    example: {
      title: "Vom Fragebogen zum Fahrplan",
      persona: "Typisches Szenario: Sondermaschinenbauer, 85 Mitarbeitende",
      problem:
        "Ein Kunde aus der Automobilindustrie schickte einen Fragebogen zur Informationssicherheit. Bei der Beantwortung stellte sich heraus, dass der Betrieb selbst wichtige Einrichtung ist. Fernwartung lief über drei verschiedene Werkzeuge.",
      solution:
        "Einstufung bestätigt und dokumentiert, Fernwartung auf einen protokollierten Zugang mit Mehr-Faktor-Anmeldung zusammengeführt, Maßnahmenplan mit Terminen, Fragebogen aus derselben Nachweisliste beantwortet.",
      result:
        "Der Fragebogen ging belegt und fristgerecht zurück. Registrierung und Selbstdeklaration greifen auf dieselben Unterlagen zu, statt bei null zu beginnen.",
    },
    faq: [
      {
        q: "Wir sind 45 Mitarbeitende, gehören aber zu einer Gruppe. Sind wir erfasst?",
        a: "Möglicherweise. Bei Beteiligungen über 25 % werden Zahlen verbundener und Partnerunternehmen zusammengerechnet – außer Ihre Netz- und Informationssysteme sind von denen der Gruppe unabhängig (§ 25 Abs. 4). Das ist der häufigste Grund, warum kleinere Maschinenbauer doch unter das Gesetz fallen, und gehört in eine dokumentierte Einzelprüfung.",
      },
      {
        q: "Müssen wir unsere Bestandsmaschinen nachrüsten?",
        a: "Nicht pauschal. Verlangt sind verhältnismäßige Maßnahmen nach dem Stand der Technik, unter Berücksichtigung der Kosten. Häufig reicht es, alte Systeme abzuschotten, Zugänge zu kontrollieren und Ausfälle einzuplanen. Was angemessen ist, ergibt die Risikoanalyse.",
      },
      {
        q: "Gilt NIS2 für die Maschinen, die wir verkaufen?",
        a: "Nein, NIS2 regelt die Sicherheit Ihres Betriebs. Für Produkte sind die Maschinenverordnung und der Cyber Resilience Act maßgeblich. Kunden fragen trotzdem nach – etwa, wie Ihr Service-Fernzugriff abgesichert ist, weil er Teil ihrer Lieferkette ist.",
      },
      {
        q: "Brauchen wir eine ISO-27001-Zertifizierung?",
        a: "Das Gesetz schreibt sie nicht vor. Sinnvoll ist sie, wenn Kunden sie verlangen. Für die gesetzlichen Pflichten reicht ein angemessenes, dokumentiertes Programm – das wir so aufbauen, dass eine spätere Zertifizierung möglich bleibt.",
      },
    ],
    relatedServices: ["it-consulting", "qualitaetssicherung-qa", "softwareentwicklung"],
    relatedSlugs: ["automotive-zulieferer", "dokumenten-automatisierung", "kundenportal"],
    nis2Hinweis:
      "Maschinenbau steht in Anlage 2 des NISG 2026. Ab 50 Beschäftigten oder mehr als 10 Mio. € Umsatz und Bilanzsumme sind Sie in aller Regel wichtige Einrichtung – mit Registrierung bis 31. Dezember 2026 und Selbstdeklaration bis 30. September 2027.",
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "automotive-zulieferer",
    icon: "car",
    title: "Automotive-Zulieferer",
    navTitle: "Automotive",
    tagline: "NIS2, TISAX und Kundenfragebögen – einmal belegt statt dreimal beantwortet.",
    summary:
      "Wer Kraftwagen oder Kfz-Teile herstellt, steht in Anlage 2 des NISG 2026 – und bekommt von Herstellern und Tier-1 zusätzlich eigene Anforderungen. Wir bringen beides auf eine gemeinsame Nachweisliste.",
    keywords: [
      "nis2 automotive",
      "nis2 kfz zulieferer",
      "nis2 automobilzulieferer österreich",
      "tisax nis2 unterschied",
      "nisg 2026 fahrzeugbau",
      "informationssicherheit zulieferer",
    ],
    metaTitle: "NIS2 für Automotive-Zulieferer",
    metaDescription:
      "Kfz-Teile-Hersteller fallen unter Anlage 2 des NISG 2026. NIS2, TISAX und Kundenfragebögen auf einer Nachweisliste – prüfbar umgesetzt, zum Fixpreis.",
    heroEyebrow: "Für Automotive-Zulieferer",
    heroHeadline: "NIS2, TISAX, Kundenfragebogen: dreimal gefragt, einmal belegt",
    heroSub:
      "Hersteller und Tier-1 fragen seit Jahren nach Informationssicherheit. Mit dem NISG 2026 kommt für viele Zulieferer eine gesetzliche Pflicht dazu. Wir ordnen die Anforderungen aus Gesetz und Kundenverträgen einer gemeinsamen Nachweisliste zu – damit Sie jede Frage nur einmal beantworten.",
    pains: [
      {
        title: "Jeder Kunde fragt anders",
        text: "Fragebögen in unterschiedlichen Formaten, Portale, Selbstauskünfte, dazu Audits. Inhaltlich überschneiden sie sich stark – beantwortet werden sie trotzdem jedes Mal von vorn.",
      },
      {
        title: "TISAX ist nicht NIS2",
        text: "Ein TISAX-Label deckt viel ab, aber nicht die gesetzlichen Pflichten: Registrierung, Meldung an das CSIRT, Selbstdeklaration, die Rolle der Geschäftsführung.",
      },
      {
        title: "Die Linie darf nicht stehen",
        text: "Bei eng getakteter Anlieferung bedeutet ein Ausfall von Stunden Lieferverzug. Notfallplanung ist hier kein Formular, sondern Lieferfähigkeit.",
      },
      {
        title: "Niemand hat den Hut auf",
        text: "Die Qualitätsabteilung kennt Audits, aber nicht Informationssicherheit. Die IT ist ausgelagert. Wer koordiniert das Ganze – und bis wann?",
      },
    ],
    solutions: [
      {
        title: "Eine Nachweisliste für alle",
        text: "Anforderungen aus dem NISG 2026, dem TISAX-Katalog und Ihren Kundenfragebögen werden gemeinsamen Maßnahmen zugeordnet. Neue Anfragen beantworten Sie aus dem Bestand.",
      },
      {
        title: "Notfallplan für Lieferfähigkeit",
        text: "Welche Systeme brauchen Sie, um liefern zu können, wie schnell sind sie wieder da, wer entscheidet? Geprobt in einer Tabletop-Übung, nicht nur aufgeschrieben.",
      },
      {
        title: "Ein Plan für IT und Qualität",
        text: "Wir sprechen die Sprache Ihres IT-Dienstleisters und Ihrer Qualitätsabteilung – und halten beide auf demselben Plan mit denselben Terminen.",
      },
    ],
    sections: [
      {
        heading: "Fahrzeugbau im NISG 2026",
        body:
          "Anlage 2 des NISG 2026 erfasst die Herstellung von Kraftwagen und Kraftwagenteilen, bezeichnet als Abschnitt C, Abteilung 29 der NACE. Dazu gehört auch die Herstellung von Teilen und Zubehör für Kraftwagen. Zulieferer, deren Haupttätigkeit dort liegt, sind ab 50 Beschäftigten oder mit mehr als 10 Mio. € Umsatz und Bilanzsumme wichtige Einrichtungen. Anders sieht es aus, wenn ein Zulieferer vor allem Metall bearbeitet oder Kunststoffteile spritzt: Dann entscheidet der jeweilige NACE-Abschnitt, und diese stehen nicht in den Anlagen. Wer Elektronik oder elektrische Ausrüstung fertigt, ist über die Abteilungen 26 und 27 wiederum erfasst. Die Zuordnung der Haupttätigkeit ist deshalb der erste und wichtigste Schritt.",
      },
      {
        heading: "Was TISAX abdeckt – und was nicht",
        body:
          "TISAX ist das Prüf- und Austauschverfahren der Automobilindustrie auf Basis des VDA-ISA-Katalogs, der sich an ISO/IEC 27001 anlehnt. Wer ein Label hat, hat den größten Teil der technischen und organisatorischen Maßnahmen bereits umgesetzt. Nicht abgedeckt sind die gesetzlichen Pflichten: die Registrierung beim Bundesamt für Cybersicherheit, die Meldung erheblicher Vorfälle binnen 24 Stunden, 72 Stunden und einem Monat, die Selbstdeklaration und die ausdrückliche Verantwortung der Geschäftsführung nach § 31. Außerdem kann der Geltungsbereich eines Labels enger sein als das, was das Gesetz erfasst – etwa ein einzelner Standort statt des ganzen Unternehmens.",
      },
      {
        heading: "Warum Notfallplanung hier anders zählt",
        body:
          "In einer Lieferkette mit knapp getakteter Anlieferung ist ein Ausfall kein IT-Problem, sondern ein Lieferproblem. Die Maßnahmen zur Aufrechterhaltung des Betriebs – Backup, Wiederherstellung, Krisenmanagement – sollten deshalb von der Lieferfähigkeit her gedacht werden: Welche Systeme braucht der Versand? Wie wird ohne ERP kommissioniert? Wer informiert den Kunden, und wann? Diese Fragen gemeinsam mit Geschäftsführung, Produktion und IT durchzuspielen, zeigt in wenigen Stunden mehr Lücken als jede Richtlinie.",
      },
    ],
    deliverables: [
      "Einstufung nach NISG 2026 mit dokumentierter Begründung",
      "Gemeinsame Nachweisliste für Gesetz, TISAX und Kundenfragebögen",
      "Notfall- und Meldeplan mit Blick auf Lieferfähigkeit",
      "Tabletop-Übung mit Bericht",
      "Unterlagen für Registrierung und Selbstdeklaration",
    ],
    example: {
      title: "Drei Anfragen, eine Antwort",
      persona: "Typisches Szenario: Kfz-Teile-Hersteller, 140 Mitarbeitende",
      problem:
        "Zwei Fragebögen von Fahrzeugherstellern und eine Selbstauskunft eines Tier-1 innerhalb eines Quartals, dazu die Frage der Geschäftsführung, ob man selbst unter das NISG 2026 fällt. Jede Anfrage bearbeitete eine andere Person.",
      solution:
        "Einstufung als wichtige Einrichtung bestätigt, die Anforderungen aller drei Anfragen und des Gesetzes in einer Nachweisliste zusammengeführt, Lücken nach Risiko priorisiert und mit Terminen hinterlegt.",
      result:
        "Die Anfragen wurden aus demselben Bestand beantwortet. Neue Fragebögen beginnen nicht mehr bei null, und die Selbstdeklaration hat ihre Grundlage.",
    },
    faq: [
      {
        q: "Wir haben ein TISAX-Label. Sind wir damit NIS2-konform?",
        a: "Nicht automatisch. Technisch und organisatorisch ist vieles abgedeckt; die gesetzlichen Pflichten wie Registrierung, Meldewege, Selbstdeklaration und die Schulung der Geschäftsführung kommen dazu. Mit Label ist der Weg aber deutlich kürzer.",
      },
      {
        q: "Wir sind Tier-2 mit 40 Mitarbeitenden. Betrifft uns das?",
        a: "Direkt meist nicht, wenn Sie auch nach Umsatz und Bilanzsumme unter den Schwellen liegen und nicht zu einer Gruppe gehören. Indirekt fast sicher: Ihre Kunden müssen die Sicherheit ihrer Lieferkette gewährleisten und geben Anforderungen an Sie weiter.",
      },
      {
        q: "Wie lange dauert es bis zu belastbaren Nachweisen?",
        a: "Für einen Zulieferer mit 20 bis 100 Mitarbeitenden rechnen wir mit vier bis acht Wochen bis zu einem belegt beantworteten Fragebogen. Die vollständige Umsetzung als wichtige Einrichtung dauert länger und wird in der Gap-Analyse geplant.",
      },
    ],
    relatedServices: ["it-consulting", "qualitaetssicherung-qa", "softwareentwicklung"],
    relatedSlugs: ["maschinenbau", "transport-logistik", "dokumenten-automatisierung"],
    nis2Hinweis:
      "Die Herstellung von Kraftwagen und Kraftwagenteilen steht in Anlage 2 des NISG 2026. Ab 50 Beschäftigten oder mehr als 10 Mio. € Umsatz und Bilanzsumme sind Sie in aller Regel wichtige Einrichtung – und als Lieferant zusätzlich von Ihren Kunden gefragt.",
  },
];

export function getIndustry(slug: string) {
  return industries.find((i) => i.slug === slug);
}
